import {
  Button,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import {
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogPaper,
} from '~/modules/matters/modals/shared';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { useToast } from '~/shared/contexts/ToastContext';
import {
  ExpertParticipationDecision,
  MatterExpertExitReason,
} from '~/gql/graphql';
import { useMatterMutations } from '~/modules/matters/useMatterMutations';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  expertId: string;
  isInvitedOnly: boolean;
  onSuccess: () => void;
}

const REASON_OPTIONS: { value: MatterExpertExitReason; label: string }[] = [
  {
    value: MatterExpertExitReason.DoesntFitExpertise,
    label: "Doesn't fit my expertise",
  },
  {
    value: MatterExpertExitReason.ScheduleConflict,
    label: 'Schedule conflict',
  },
  {
    value: MatterExpertExitReason.ConflictOfInterest,
    label: 'Conflict of interest',
  },
  {
    value: MatterExpertExitReason.OutsideSpecialty,
    label: 'Case outside my specialty',
  },
  { value: MatterExpertExitReason.Other, label: 'Other' },
];

function isExitReason(value: string): value is MatterExpertExitReason {
  return Object.values(MatterExpertExitReason).includes(
    value as MatterExpertExitReason,
  );
}

const DeclineMatterModal = ({
  isOpen,
  onClose,
  matterId,
  expertId,
  isInvitedOnly,
  onSuccess,
}: Props) => {
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showToast } = useToast();
  const {
    removeExpertFromMatter,
    updateExpertParticipation,
    isRemovingExpert,
    isUpdatingExpertParticipation,
  } = useMatterMutations();

  const isSubmitting = isRemovingExpert || isUpdatingExpertParticipation;

  const [reason, setReason] = useState<MatterExpertExitReason | ''>('');
  const [otherText, setOtherText] = useState('');
  const [comment, setComment] = useState('');

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setReason('');
    setOtherText('');
    setComment('');
    onClose();
  };

  const buildExitComment = (): string | undefined => {
    const trimmedComment = comment.trim();
    const trimmedOther = otherText.trim();
    if (reason === MatterExpertExitReason.Other && trimmedOther) {
      return trimmedComment
        ? `${trimmedOther}\n\n${trimmedComment}`
        : trimmedOther;
    }
    return trimmedComment || undefined;
  };

  const handleConfirm = async () => {
    if (!isExitReason(reason)) {
      return;
    }
    if (reason === MatterExpertExitReason.Other && !otherText.trim()) {
      showToast('Please provide details for "Other".', 'error');
      return;
    }
    const exitComment = buildExitComment();
    const exitReason = reason;
    try {
      if (isInvitedOnly) {
        await updateExpertParticipation({
          matterId,
          expertId,
          status: ExpertParticipationDecision.Declined,
          exitReason,
          exitComment,
        });
      } else {
        await removeExpertFromMatter({
          matterId,
          expertId,
          exitReason,
          exitComment,
        });
      }
      showToast(
        isInvitedOnly
          ? 'You have declined this matter. The attorney has been notified.'
          : 'You have been removed from this matter.',
      );
      handleClose();
      onSuccess();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'decline_or_leave_matter' },
        extra: { matterId, expertId, isInvitedOnly },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      showToast(message, 'error');
    }
  };

  const title = isInvitedOnly
    ? 'Decline matter?'
    : 'Remove yourself from matter?';
  const subtitle = isInvitedOnly
    ? 'You will be removed from this matter and the attorney will be notified. Please select a reason.'
    : 'You will be removed from this matter. Please select a reason.';
  const confirmLabel = isInvitedOnly ? 'Decline' : 'Leave matter';

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isFullScreen}
      PaperComponent={StyledDialogPaper}
    >
      <ModalHeader title={title} subtitle={subtitle} onClose={handleClose} />
      <StyledDialogContent>
        <ContentStack>
          <FormControl fullWidth size="medium" required>
            <InputLabel id="decline-reason-label">Reason</InputLabel>
            <Select
              labelId="decline-reason-label"
              id="decline-reason"
              value={reason}
              label="Reason"
              onChange={(event) => {
                const value = event.target.value;
                if (isExitReason(value)) {
                  setReason(value);
                }
              }}
            >
              {REASON_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {reason === MatterExpertExitReason.Other && (
            <TextField
              label="Please specify"
              placeholder="Enter reason"
              value={otherText}
              onChange={(event) => setOtherText(event.target.value)}
              multiline
              minRows={2}
              fullWidth
              variant="outlined"
              size="medium"
              required
            />
          )}
          <TextField
            label="Comment for attorney (optional)"
            placeholder="Add a message the attorney will see"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            variant="outlined"
            size="medium"
          />
        </ContentStack>
      </StyledDialogContent>
      <StyledDialogActions>
        <FullWidthConfirmButton
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={
            isSubmitting ||
            !isExitReason(reason) ||
            (reason === MatterExpertExitReason.Other && !otherText.trim())
          }
        >
          {isSubmitting ? `${confirmLabel}…` : confirmLabel}
        </FullWidthConfirmButton>
      </StyledDialogActions>
    </Dialog>
  );
};

const ContentStack = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  overflow: 'visible',
  gap: theme.spacing(2),
}));

const FullWidthConfirmButton = styled(Button)(({ theme }) => ({
  width: '100%',
  minHeight: 44,
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
}));

export default DeclineMatterModal;
